import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Space, Spin, Table, Tag, Typography } from 'antd';
import { ReloadOutlined, InboxOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchOrderWarehouseSummary } from '../api/orderWarehouseApi';
import type { OrderCostPlan, OrderCostPlanLine, OrderWarehouseLine, OrderWarehouseSummary } from '../../../types/orderWarehouseSummary';
import { formatUnit } from '../../../utils/units';
import { formatCurrency, formatCurrencyUnitPrice } from '../../../utils/currency';
import { formatUnitPrice } from '../../../utils/money';
import { formatDate } from '../../../utils/dateUtils';
import './OrderWarehouseSection.css';

interface Props {
  orderId: string;
  canViewMargin: boolean;
  refreshKey?: unknown;
  onRefreshOrder?: () => Promise<unknown>;
}

const formatQuantity = (value: number, unit: string | null) =>
  `${value.toLocaleString('pl-PL', { maximumFractionDigits: 3 })} ${formatUnit(unit, 'pl')}`;

/** One order line of the cost plan: issued, allocated batches, average and unknown parts. */
function PlanLine({ line, name }: { line: OrderCostPlanLine; name: string }) {
  const { t } = useTranslation(['orders']);
  const amount = (value: number) => formatCurrency(value, 'PLN');
  const empty = line.issuedCostPLN === 0 && line.fifo.length === 0 && line.averageCostPLN == null && !line.costPending;
  return <div className="order-warehouse-plan-line">
    <strong className="order-warehouse-plan-name">{name}</strong>
    <ul className="order-warehouse-plan-parts">
      {line.issuedCostPLN > 0 && <li>
        <span>{t('orders:warehouseFlow.lineIssued')}</span><span className="order-warehouse-plan-amount">{amount(line.issuedCostPLN)}</span>
      </li>}
      {line.fifo.map((part, index) => <li key={`${part.batchNumber}-${index}`}>
        <span>
          {t('orders:warehouseFlow.lineAllocated', { quantity: formatQuantity(part.quantity, part.unit) })}
          {' · '}{t('orders:warehouseFlow.batchLabel', { pz: part.pzNumber ?? '—', batch: part.batchNumber ?? '—' })}
          {' '}<Tag>{t(`orders:warehouseFlow.source.${part.source}`)}</Tag>
        </span>
        <span className="order-warehouse-plan-amount">{amount(part.costPLN)}</span>
      </li>)}
      {line.averageCostPLN != null && line.averageQuantity != null && <li>
        <span>
          {t('orders:warehouseFlow.lineAverage', { quantity: formatQuantity(line.averageQuantity, line.averageUnit) })}
          {line.averagePurchasePricePLN != null && <span className="order-warehouse-average-price">
            {' × '}{formatCurrencyUnitPrice(line.averagePurchasePrice ?? line.averagePurchasePricePLN, line.averagePurchaseCurrency || 'PLN', 'pl')}
            {line.averagePurchasePriceUnit && ` / ${formatUnit(line.averagePurchasePriceUnit, 'pl')}`}
            {line.averageExchangeRate != null && ` ${t('orders:warehouseFlow.averageRate', {
              rate: formatUnitPrice(line.averageExchangeRate, 'pl'),
              date: line.averageExchangeRateDate ? formatDate(line.averageExchangeRateDate) : t('orders:warehouseFlow.averageRateOrder'),
            })}`}
          </span>}
          {' '}<Tag color="gold">{t('orders:warehouseFlow.estimated')}</Tag>
        </span>
        <span className="order-warehouse-plan-amount">{amount(line.averageCostPLN)}</span>
      </li>}
      {line.costPending && <li className="order-warehouse-plan-unknown">
        <span>
          {line.pendingQuantity != null
            ? t('orders:warehouseFlow.lineUnknown', { quantity: formatQuantity(line.pendingQuantity, line.unit) })
            : t('orders:warehouseFlow.lineUnknownNoQuantity')}
          {line.pendingReason && ` — ${t(`orders:warehouseFlow.pendingReason.${line.pendingReason}`)}`}
        </span>
        <span className="order-warehouse-plan-amount">—</span>
      </li>}
      {empty && <li><span>{t('orders:warehouseFlow.lineNothingOpen')}</span><span className="order-warehouse-plan-amount">{amount(0)}</span></li>}
      {line.warnings.map(warning => <li key={warning} className="order-warehouse-plan-unknown">
        <span>{t(`orders:warehouseFlow.lineWarning.${warning}`)}</span>
      </li>)}
    </ul>
  </div>;
}

function CostPlanSection({ plan, summary }: { plan: OrderCostPlan; summary: OrderWarehouseSummary }) {
  const { t } = useTranslation(['orders']);
  const finance = summary.finance!;
  const amount = (value: number | null | undefined) => value == null ? '—' : formatCurrency(value, 'PLN');
  const lineName = (id: string) => summary.lines.find(product => product.id === id)?.name ?? '—';
  return <>
    <Typography.Paragraph type="secondary">
      {t(plan.closedByInvoice ? 'orders:warehouseFlow.closedByInvoice'
        : plan.participates ? 'orders:warehouseFlow.allocationHelp' : 'orders:warehouseFlow.draftNoAllocation')}
    </Typography.Paragraph>
    <div className="order-warehouse-plan-lines">
      {plan.lines.map(line => <PlanLine key={line.id} line={line} name={lineName(line.id)} />)}
    </div>
    <dl className="order-warehouse-costs">
      <div><dt title={t('orders:warehouseFlow.fifoCostHelp')}>{t('orders:warehouseFlow.fifoCost')}</dt><dd>{amount(plan.issuedCostPLN)}</dd></div>
      <div><dt>{t('orders:warehouseFlow.availableStockCost')}</dt><dd>{amount(plan.stockCostPLN)}</dd></div>
      {plan.usesAverage && <div><dt>{t('orders:warehouseFlow.averageTotal')}</dt><dd>{amount(plan.averageCostPLN)}</dd></div>}
      <div><dt>{t('orders:warehouseFlow.transportCost')}</dt><dd>{amount(finance.transportCostPLN)}</dd></div>
      <div><dt>{t('orders:warehouseFlow.otherCost')}</dt><dd>{amount(finance.otherCostPLN)}</dd></div>
    </dl>
    {finance.costProvisional && <Alert type="warning" showIcon message={t('orders:warehouseFlow.costProvisional')} />}
    {plan.costPending && <Alert type="warning" showIcon message={t('orders:warehouseFlow.averageMissing')} />}
    {plan.totalCostPLN != null && <div className="order-warehouse-result">
      <div><span>{t('orders:warehouseFlow.plannedCost')}</span><strong>{amount(plan.totalCostPLN)}</strong></div>
      <div><span>{t('orders:warehouseFlow.plannedMargin')}</span><strong>{amount(plan.marginPLN)}
        {plan.marginPercent != null && <small> ({plan.marginPercent.toLocaleString('pl-PL')}%)</small>}</strong></div>
    </div>}
  </>;
}

export default function OrderWarehouseSection({ orderId, canViewMargin, refreshKey, onRefreshOrder }: Props) {
  const { t } = useTranslation(['orders', 'common', 'wz']);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{ orderId: string; summary?: OrderWarehouseSummary; failed?: boolean }>();
  useEffect(() => {
    let active = true;
    setState(undefined);
    fetchOrderWarehouseSummary(orderId).then(
      summary => { if (active) setState({ orderId, summary }); },
      () => { if (active) setState({ orderId, failed: true }); },
    );
    return () => { active = false; };
  }, [orderId, revision, refreshKey]);
  // A return from the PZ/catalog screen must refresh the live valuation.
  useEffect(() => {
    const refresh = () => {
      if (!document.hidden) {
        if (onRefreshOrder) void onRefreshOrder().catch(() => setRevision(value => value + 1));
        else setRevision(value => value + 1);
      }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [onRefreshOrder]);
  const summary = state?.orderId === orderId ? state.summary : undefined;
  const failed = state?.orderId === orderId && state.failed;
  const plan = canViewMargin ? summary?.finance?.plan : undefined;
  const noStock = !!summary?.lines.some(line => (line.remainingQuantity ?? 0) > 0) && summary.lines.every(line => line.availableQuantity === 0);
  const quantity = (value: number | null, line: OrderWarehouseLine) => value === null
    ? t('orders:warehouseFlow.unknownAvailability')
    : `${value.toLocaleString('pl-PL', { maximumFractionDigits: 4 })} ${formatUnit(line.unit, 'pl')}`;
  return <Card className="order-warehouse" title={<Space><InboxOutlined />{t('orders:warehouseFlow.fulfillment')}</Space>}
    extra={<Button type="text" icon={<ReloadOutlined />} aria-label={t('orders:warehouseFlow.refresh')}
      onClick={() => setRevision(value => value + 1)} />}>
    <div className="order-warehouse-intro">
      <Typography.Text type="secondary">{t('orders:warehouseFlow.fulfillmentHelp')}</Typography.Text>
      {noStock && <Tag>{t('orders:warehouseFlow.noStock')}</Tag>}
    </div>
    {failed ? <Alert type="warning" showIcon message={t('orders:warehouseFlow.loadError')}
      action={<Button onClick={() => setRevision(value => value + 1)}>{t('common:retry')}</Button>} />
      : !summary ? <Spin aria-label={t('common:messages.loading')} /> : <>
        <Table<OrderWarehouseLine> dataSource={summary.lines} rowKey="id" size="small" pagination={false}
          scroll={{ x: 650 }} columns={[
            { title: t('orders:warehouseFlow.product'), dataIndex: 'name', key: 'name' },
            ...(['orderedQuantity', 'availableQuantity', 'issuedQuantity', 'remainingQuantity', 'invoicedQuantity'] as const).map(key => ({
              title: t(`orders:warehouseFlow.${key}`), key, align: 'right' as const,
              render: (_: unknown, line: OrderWarehouseLine) => <span className="order-warehouse-quantity">{quantity(line[key], line)}</span>,
            })),
          ]} />
        {summary.documents.length > 0 && <Space wrap className="order-warehouse-documents">
          {summary.documents.map(document => <Link key={document.id} to={`/app/wz/${encodeURIComponent(document.id)}`}>
            {document.wzNumber}
          </Link>)}
        </Space>}
        {canViewMargin && summary.finance && <section className="order-warehouse-valuation">
          <div className="order-warehouse-valuation-heading">
            <Typography.Title level={5}>{t('orders:warehouseFlow.planningTitle')}</Typography.Title>
            <Space wrap>
              {plan && !plan.participates && !plan.closedByInvoice && <Tag>{t('orders:warehouseFlow.notConfirmed')}</Tag>}
              {plan?.usesAverage && <Tag color="gold">{t('orders:warehouseFlow.estimated')}</Tag>}
            </Space>
          </div>
          {plan ? <CostPlanSection plan={plan} summary={summary} />
            : summary.finance.costPending && <Alert type="warning" showIcon message={t('orders:warehouseFlow.costPending')} />}
        </section>}
      </>}
    <div className="order-warehouse-actions">
      <Link to={`/app/wz/new?orderId=${encodeURIComponent(orderId)}`}>
        <Button type={noStock ? 'default' : 'primary'}>{t('orders:warehouseFlow.issueGoods')}</Button>
      </Link>
    </div>
  </Card>;
}
